import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Gamepad2, Briefcase, Video, Bitcoin, GraduationCap, Dumbbell, Heart, Utensils, Music, Camera } from "lucide-react";
import { useState } from "react";

const Examples = () => {
  const categories = [
    { id: "all", name: "All", icon: null },
    { id: "gaming", name: "Gaming", icon: Gamepad2 },
    { id: "business", name: "Business", icon: Briefcase },
    { id: "vlogging", name: "Vlogging", icon: Video },
    { id: "crypto", name: "Crypto", icon: Bitcoin },
    { id: "education", name: "Education", icon: GraduationCap },
    { id: "fitness", name: "Fitness", icon: Dumbbell },
    { id: "lifestyle", name: "Lifestyle", icon: Heart },
    { id: "food", name: "Food", icon: Utensils },
    { id: "music", name: "Music", icon: Music },
    { id: "photography", name: "Photography", icon: Camera },
  ];

  const [activeCategory, setActiveCategory] = useState("all");

  const examples = [
    { category: "gaming", title: "How I Reached Radiant in 30 Days", views: "2.4M views", ctr: "12.4% CTR" },
    { category: "gaming", title: "This Pro Player Taught Me His Secret", views: "1.8M views", ctr: "10.2% CTR" },
    { category: "business", title: "My $1M Side Hustle Strategy Revealed", views: "890K views", ctr: "11.8% CTR" },
    { category: "business", title: "How to Start a Business with $0", views: "2.1M views", ctr: "9.7% CTR" },
    { category: "vlogging", title: "Day in My Life in Tokyo Japan", views: "1.2M views", ctr: "8.9% CTR" },
    { category: "vlogging", title: "Moving to NYC Changed Everything", views: "980K views", ctr: "10.1% CTR" },
    { category: "crypto", title: "This Altcoin Will 100x in 2025", views: "567K views", ctr: "15.2% CTR" },
    { category: "crypto", title: "Bitcoin Price Prediction Shocker", views: "1.1M views", ctr: "13.8% CTR" },
    { category: "education", title: "Learn Python in 24 Hours", views: "3.1M views", ctr: "7.8% CTR" },
    { category: "education", title: "The Study Technique That Changed My Life", views: "2.5M views", ctr: "9.2% CTR" },
    { category: "fitness", title: "30 Day Body Transformation Results", views: "1.8M views", ctr: "11.5% CTR" },
    { category: "fitness", title: "I Worked Out Like The Rock for 30 Days", views: "2.3M views", ctr: "12.1% CTR" },
    { category: "lifestyle", title: "Morning Routine That Changed My Life", views: "1.5M views", ctr: "8.4% CTR" },
    { category: "food", title: "I Tried Every Michelin Star Restaurant", views: "890K views", ctr: "9.8% CTR" },
    { category: "music", title: "I Made a Song in 1 Hour Challenge", views: "1.2M views", ctr: "10.3% CTR" },
    { category: "photography", title: "Camera Settings Every Pro Uses", views: "780K views", ctr: "8.9% CTR" },
  ];

  const filteredExamples = activeCategory === "all" 
    ? examples 
    : examples.filter(e => e.category === activeCategory);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      gaming: "from-red-500 to-orange-500",
      business: "from-blue-500 to-cyan-500",
      vlogging: "from-pink-500 to-purple-500",
      crypto: "from-yellow-500 to-orange-500",
      education: "from-green-500 to-teal-500",
      fitness: "from-purple-500 to-pink-500",
      lifestyle: "from-rose-500 to-pink-500",
      food: "from-orange-500 to-red-500",
      music: "from-violet-500 to-purple-500",
      photography: "from-cyan-500 to-blue-500",
    };
    return colors[category] || "from-primary to-primary";
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="section-padding relative overflow-hidden">
          <div className="absolute inset-0 hero-glow" />
          <div className="container-custom relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <span className="text-sm font-semibold text-brand-purple uppercase tracking-wider mb-4 block">
                Examples Gallery
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Real Thumbnails,{" "}
                <span className="gradient-text">Real Results</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Browse through hundreds of AI-generated thumbnails across every niche. 
                All created with ThumbGenius.
              </p>
            </div>
          </div>
        </section>

        {/* Filter */}
        <section className="pb-8">
          <div className="container-custom">
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                    activeCategory === cat.id
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border bg-card/50 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  }`}
                >
                  {cat.icon && <cat.icon className="w-4 h-4" />}
                  <span className="text-sm font-medium">{cat.name}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Examples Grid */}
        <section className="section-padding pt-8">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredExamples.map((example, index) => (
                <div
                  key={index}
                  className="group relative rounded-2xl overflow-hidden border border-border bg-card hover:border-primary/30 transition-all duration-300"
                >
                  {/* Thumbnail */}
                  <div className="aspect-video bg-gradient-to-br from-secondary to-muted relative overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${getCategoryColor(example.category)} opacity-40`} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-2xl bg-background/30 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                        {(() => {
                          const cat = categories.find(c => c.id === example.category);
                          if (cat?.icon) {
                            const IconComponent = cat.icon;
                            return <IconComponent className="w-8 h-8 text-foreground" />;
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                    <div className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-background/80 backdrop-blur-sm text-xs font-medium capitalize">
                      {example.category}
                    </div>
                    <div className="absolute top-3 right-3 px-2 py-1 rounded-lg gradient-bg text-xs font-medium text-primary-foreground">
                      {example.ctr}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h4 className="font-semibold mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                      {example.title}
                    </h4>
                    <p className="text-sm text-muted-foreground">{example.views}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center p-12 rounded-3xl bg-card border border-border relative overflow-hidden">
              <div className="absolute inset-0 hero-glow opacity-50" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Ready to Create Your Own?
                </h2>
                <p className="text-muted-foreground mb-8">
                  Start generating thumbnails like these in seconds.
                </p>
                <Link to="/signup">
                  <Button variant="hero" size="xl" className="group">
                    Start Creating Free
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Examples;
